/* global grecaptcha */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import ToasterComponent from 'shared/components/ToasterComponent';
import SuccessModal from 'formal_quote/inline_formal_quote/components/SuccessModal';
import RecommendedProduct from 'formal_quote/inline_formal_quote/components/RecommendedProduct';
import LicenseScopeCards from 'formal_quote/components/LicenseScopeCards';
import AddressFields from 'formal_quote/components/AddressFields';
import { selectInlineFormalQuote, selectShowForm } from 'formal_quote/inline_formal_quote/redux/selectors';
import { createFormalQuote } from 'formal_quote/thunks';
import { FORMAL_QUOTE_OPTIONS } from 'formal_quote/options';
import { validateForm } from 'formal_quote/validator';
import { useAddressCountries } from 'formal_quote/hooks/useAddressCountries';
import { selectSelectedVariant, selectProduct } from 'software_products/redux/selectors';
import { mapProductSlugToSoftwareType, SOFTWARE_TYPES } from 'formal_quote/utils';
import {
  setFormData,
  setErrors,
  clearErrors,
  showSuccessModal,
  resetForm,
  hideFormalQuoteForm,
} from 'formal_quote/inline_formal_quote/redux/slice';

const InlineFormalQuoteForm = () => {
  const dispatch = useDispatch();
  const { formData, errors, loading } = useSelector(selectInlineFormalQuote);
  const showForm = useSelector(selectShowForm);
  const selectedVariant = useSelector(selectSelectedVariant);
  const product = useSelector(selectProduct);
  const countries = useAddressCountries();

  // Get option values from selected variant
  const optionValues = selectedVariant?.option_values;
  const variantLicenseType = optionValues?.find((ov) => ov.option_type_name === 'license-type')?.name;
  const activationMethod = optionValues?.find((ov) => ov.option_type_name === 'activation_method')?.name;
  const numOfUsers = optionValues?.find((ov) => ov.option_type_name === 'num_of_users')?.name;
  const shouldShowDeliveryAddress = !formData.same_as_billing;
  const softwareType = mapProductSlugToSoftwareType(product?.slug);
  const isViewmateBusiness = softwareType === SOFTWARE_TYPES.viewmate_business;
  const recaptchaWidgetId = useRef(null);
  const [recaptchaCompleted, setRecaptchaCompleted] = useState(false);

  // Preserve delivery address values when toggling checkbox
  const [preservedDeliveryAddress, setPreservedDeliveryAddress] = useState({
    delivery_address: '',
    delivery_city: '',
    delivery_country: '',
    delivery_state: '',
    delivery_country_region: '',
    delivery_zip_code: '',
  });

  // Own v2 checkbox (mirrored from FormalQuoteForm); never bare getResponse/reset.
  useEffect(() => {
    const checkGrecaptcha = () => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
        grecaptcha.ready(() => {
          if (recaptchaWidgetId.current !== null) return;

          recaptchaWidgetId.current = grecaptcha.render(
            'inline-formal-quote-recaptcha',
            {
              sitekey: Spree.recaptchaSiteKey,
              callback: () => setRecaptchaCompleted(true),
            },
          );
        });
      } else {
        setTimeout(checkGrecaptcha, 100);
      }
    };

    checkGrecaptcha();
  }, []);

  useEffect(() => {
    if (!softwareType) return;
    dispatch(setFormData({ software_type: softwareType }));
  }, [softwareType, dispatch]);

  useEffect(() => {
    if (isViewmateBusiness) {
      if (!formData.license_scope) {
        dispatch(setFormData({ license_scope: 'standard' }));
      }
    } else if (formData.license_scope) {
      dispatch(setFormData({ license_scope: null }));
    }
  }, [isViewmateBusiness, formData.license_scope, dispatch]);

  // Same as billing address toggle
  useEffect(() => {
    if (formData.same_as_billing) {
      // Save current delivery address values before copying
      setPreservedDeliveryAddress({
        delivery_address: formData.delivery_address || '',
        delivery_city: formData.delivery_city || '',
        delivery_country: formData.delivery_country || '',
        delivery_state: formData.delivery_state || '',
        delivery_country_region: formData.delivery_country_region || '',
        delivery_zip_code: formData.delivery_zip_code || '',
      });

      // Copy billing address to delivery address
      dispatch(
        setFormData({
          delivery_address: formData.billing_address,
          delivery_city: formData.billing_city,
          delivery_country: formData.billing_country,
          delivery_state: formData.billing_state,
          delivery_country_region: formData.billing_country_region,
          delivery_zip_code: formData.billing_zip_code,
        }),
      );
    } else {
      // Restore preserved delivery address values
      dispatch(
        setFormData({
          delivery_address: preservedDeliveryAddress.delivery_address,
          delivery_city: preservedDeliveryAddress.delivery_city,
          delivery_country: preservedDeliveryAddress.delivery_country,
          delivery_state: preservedDeliveryAddress.delivery_state,
          delivery_country_region:
            preservedDeliveryAddress.delivery_country_region,
          delivery_zip_code: preservedDeliveryAddress.delivery_zip_code,
        }),
      );
    }
  }, [formData.same_as_billing, dispatch]);

  // Copy billing to delivery when checkbox is checked
  useEffect(() => {
    if (formData.same_as_billing) {
      dispatch(
        setFormData({
          delivery_address: formData.billing_address || '',
          delivery_city: formData.billing_city || '',
          delivery_country: formData.billing_country || '',
          delivery_state: formData.billing_state || '',
          delivery_country_region: formData.billing_country_region || '',
          delivery_zip_code: formData.billing_zip_code || '',
        }),
      );
    }
  }, [
    formData.billing_address,
    formData.billing_city,
    formData.billing_country,
    formData.billing_state,
    formData.billing_country_region,
    formData.billing_zip_code,
    formData.same_as_billing,
    dispatch,
  ]);

  // Update preserved values when delivery address fields change
  useEffect(() => {
    if (!formData.same_as_billing) {
      setPreservedDeliveryAddress({
        delivery_address: formData.delivery_address || '',
        delivery_city: formData.delivery_city || '',
        delivery_country: formData.delivery_country || '',
        delivery_state: formData.delivery_state || '',
        delivery_country_region: formData.delivery_country_region || '',
        delivery_zip_code: formData.delivery_zip_code || '',
      });
    }
  }, [
    formData.delivery_address,
    formData.delivery_city,
    formData.delivery_country,
    formData.delivery_state,
    formData.delivery_country_region,
    formData.delivery_zip_code,
    formData.same_as_billing,
  ]);

  useEffect(() => {
    if (!showForm || !variantLicenseType) return;

    let numOfUsersValue = '1';
    if (numOfUsers) {
      [numOfUsersValue] = numOfUsers.split('-');
    }

    const isNetwork = variantLicenseType === 'network';
    const usesDongle = activationMethod === 'usb-dongle';

    dispatch(
      setFormData({
        delivery_method: usesDongle && !isNetwork ? 'ground_shipping' : 'instant_download',
        portability_option: usesDongle && !isNetwork ? 'any_machine' : 'not_needed',
        license_type: isNetwork ? 'network' : 'standalone',
        number_of_users: numOfUsersValue,
      }),
    );
  }, [showForm, variantLicenseType, activationMethod, numOfUsers, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(setFormData({ [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      dispatch(clearErrors());
    }
  };

  const handleCountryChange = (prefix) => (event) => {
    const countryField = `${prefix}_country`;
    const stateField = `${prefix}_state`;
    dispatch(setFormData({ [countryField]: event.target.value, [stateField]: '' }));
    if (errors[countryField] || errors[stateField]) {
      dispatch(clearErrors());
    }
  };

  const handleLicenseScopeChange = (e) => {
    const { value } = e.target;
    const usersCount = Number(formData.number_of_users);
    let numberOfUsers = '';
    if (value === 'site') {
      numberOfUsers = Number.isInteger(usersCount) && usersCount >= 10 ? formData.number_of_users : '10';
    } else {
      numberOfUsers = Number.isInteger(usersCount) && usersCount >= 1 && usersCount <= 5
        ? formData.number_of_users
        : '5';
    }

    dispatch(setFormData({ license_scope: value, number_of_users: numberOfUsers }));
    if (errors.license_scope || errors.number_of_users) {
      dispatch(clearErrors());
    }
  };

  const onSubmit = async () => {
    const data = {
      ...formData,
    };

    const newErrors = validateForm(data, {
      shouldShowNumberOfUsers: isViewmateBusiness,
      shouldShowLicenseScope: isViewmateBusiness,
    }, countries);

    if (Object.keys(newErrors).length > 0) {
      dispatch(setErrors(newErrors));
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      await dispatch(
        createFormalQuote({
          recaptchaWidgetId: recaptchaWidgetId.current,
          formal_quote: {
            ...data,
          },
        }),
      ).unwrap();

      dispatch(resetForm());
      dispatch(hideFormalQuoteForm());
      dispatch(showSuccessModal());
      setPreservedDeliveryAddress({
        delivery_address: '',
        delivery_country_region: '',
        delivery_zip_code: '',
      });
      setRecaptchaCompleted(false);
      if (grecaptcha?.reset && recaptchaWidgetId.current !== null) {
        grecaptcha.reset(recaptchaWidgetId.current);
      }
    } catch (error) {
      setRecaptchaCompleted(false);
      if (
        typeof grecaptcha !== 'undefined' &&
        grecaptcha.reset &&
        recaptchaWidgetId.current !== null
      ) {
        grecaptcha.reset(recaptchaWidgetId.current);
      }

      if (error?.errors && !Array.isArray(error.errors)) {
        const serverErrors = Object.keys(error.errors).reduce((fieldErrors, field) => ({
          ...fieldErrors,
          [field]: Array.isArray(error.errors[field]) ? error.errors[field].join(', ') : error.errors[field],
        }), {});
        dispatch(setErrors(serverErrors));
      }

      toast.error(error?.error || 'An error occurred. Please try again later.');
    }
  };

  const renderField = (name, label, type = 'text', required = true) => (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <input
        type={type}
        id={name}
        disabled={loading}
        name={name}
        className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
        value={formData[name]}
        onChange={handleInputChange}
        required={required}
      />
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </div>
  );

  const renderCheckbox = (name, label) => (
    <div className="form-check d-flex align-items-center">
      <input
        type="checkbox"
        id={name}
        name={name}
        disabled={loading}
        className="form-check-input"
        checked={formData[name] || false}
        onChange={(e) => {
          const { name: fieldName, checked } = e.target;
          dispatch(setFormData({ [fieldName]: checked }));
        }}
      />
      <label htmlFor={name} className="form-check-label text-nowrap">
        {label}
      </label>
    </div>
  );

  const renderRadioGroup = (name, label, options, showIcons = false) => (
    <div className="row">
      <div className="col-6">
        <div className="checks-label">{label}</div>
      </div>
      <div className="col-6">
        <div className="container-fluid">
          <div className="row">
            {options.map((option) => (
              <div
                key={option.value}
                className="col-12 col-sm-6 d-flex align-items-center"
              >
                {showIcons && option.icon && (
                  <div className="icon-container">
                    <img
                      src={Spree.image_assets[option.icon]}
                      className="img-fluid"
                      alt={option.label}
                    />
                  </div>
                )}
                <div className="form-check">
                  <input
                    type="radio"
                    id={`${name}_${option.value}`}
                    name={name}
                    disabled={loading}
                    value={option.value}
                    className={`form-check-input ${errors[name] ? 'is-invalid' : ''}`}
                    checked={formData[name] === option.value}
                    onChange={handleInputChange}
                    aria-labelledby={`${name}_${option.value}_label`}
                  />
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    htmlFor={`${name}_${option.value}`}
                    className="form-check-label"
                    id={`${name}_${option.value}_label`}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: option.label }}
                  />
                  {option.badge && (
                    <span className="badge">{option.badge}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {errors[name] && (
        <div className="invalid-feedback d-block">{errors[name]}</div>
      )}
    </div>
  );

  return (
    <div className="inline-formal-quote-form">
      {isViewmateBusiness && (
        <>
          <LicenseScopeCards
            value={formData.license_scope}
            onChange={handleLicenseScopeChange}
            disabled={loading}
            error={errors.license_scope}
          />
          <LicenseScopeCards
            value={formData.license_type}
            onChange={handleInputChange}
            disabled={loading}
            error={errors.license_type}
            inputName="license_type"
            legend="License Type"
            options={FORMAL_QUOTE_OPTIONS.viewmate_business_license_type}
          />
          <div className="row">
            <div className="col-12 col-md-6">
              <div className="form-group">
                <label htmlFor="number_of_users" className="form-label">
                  {formData.license_scope === 'site'
                    ? 'Potential Number of Users'
                    : 'Number of Licenses'}{' '}
                  <span className="text-danger">*</span>
                </label>
                <p className="formal-quote-field-description font-medium">
                  {formData.license_scope === 'site'
                    ? 'Organization-wide coverage is available in flexible user ranges for businesses of different sizes.'
                    : 'Choose the number of standalone or network licenses needed.'}
                </p>
                {formData.license_scope === 'site' ? (
                  <input
                    type="number"
                    id="number_of_users"
                    name="number_of_users"
                    min="10"
                    disabled={loading}
                    className={`form-control ${errors.number_of_users ? 'is-invalid' : ''}`}
                    value={formData.number_of_users || ''}
                    onChange={handleInputChange}
                    required
                  />
                ) : (
                  <select
                    id="number_of_users"
                    name="number_of_users"
                    disabled={loading}
                    className={`form-control ${errors.number_of_users ? 'is-invalid' : ''}`}
                    value={formData.number_of_users || ''}
                    onChange={handleInputChange}
                    required
                  >
                    {Object.entries(FORMAL_QUOTE_OPTIONS.viewmate_business_number_of_users).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                )}
                {errors.number_of_users && (
                  <div className="invalid-feedback">{errors.number_of_users}</div>
                )}
              </div>
            </div>
            <div className="col-12 col-md-6 d-flex align-items-end">
              <div className="w-100">{renderField('company_name', 'Company Name')}</div>
            </div>
          </div>
        </>
      )}

      <div className="row">
        <div className="col-md-6">
          {renderField('recipient_name', 'Name who receives the quote?')}
        </div>
        <div className="col-md-6">{renderField('email', 'Email', 'email')}</div>
      </div>

      <div className="row">
        <div className="col-md-6">
          {isViewmateBusiness
            ? renderField('job_title', 'Job Title', 'text', false)
            : renderField('company_name', 'Company Name')}
        </div>
        <div className="col-md-6">
          {renderField('phone_number', 'Phone Number', 'tel')}
        </div>
      </div>

      {!isViewmateBusiness && (() => {
        // Show delivery options based on the variant rules
        let deliveryOptions;
        if (formData.delivery_method === 'instant_download') {
          // Only instant download available
          deliveryOptions = [
            {
              value: 'instant_download',
              label: FORMAL_QUOTE_OPTIONS.delivery_method.instant_download,
            },
          ];
        } else {
          // Shipping options available (standalone + usb-dongle)
          deliveryOptions = [
            {
              value: 'ground_shipping',
              label: FORMAL_QUOTE_OPTIONS.delivery_method.ground_shipping,
            },
            {
              value: '2_day_shipping',
              label: FORMAL_QUOTE_OPTIONS.delivery_method['2_day_shipping'],
            },
          ];
        }
        return renderRadioGroup('delivery_method', 'Delivery Method', deliveryOptions);
      })()}

      <div className="row">
        <div className="col-12 col-md-6 mt-md-3">
          <div className="checks-label">Billing Address</div>
          <AddressFields
            prefix="billing"
            countries={countries}
            formData={formData}
            errors={errors}
            loading={loading}
            onChange={handleInputChange}
            onCountryChange={handleCountryChange('billing')}
          />
        </div>

        <div className="col-12 col-md-6 mt-md-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="checks-label">Delivery Address</div>
            {renderCheckbox('same_as_billing', 'Same as Billing Address')}
          </div>
          {shouldShowDeliveryAddress && (
            <AddressFields
              prefix="delivery"
              countries={countries}
              formData={formData}
              errors={errors}
              loading={loading}
              onChange={handleInputChange}
              onCountryChange={handleCountryChange('delivery')}
            />
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <RecommendedProduct title="You're Requesting a Quote For:" />
        </div>
      </div>

      <div className="row">
        <div className="col-12 text-center mt-5">
          <div className="mb-3 d-flex justify-content-center">
            <div
              id="inline-formal-quote-recaptcha"
              data-sitekey={Spree.recaptchaSiteKey}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={loading || !recaptchaCompleted}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
          <p className="text-muted mt-5">
            *The quotation will be processed in the next 12 hours
          </p>
        </div>
      </div>
      <SuccessModal />
      <ToasterComponent />
    </div>
  );
};

export default InlineFormalQuoteForm;
